import {
  Box,
  Icon,
  Text,
  Tooltip,
  useBreakpoint,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { AvailableModules } from 'generated/featureDefinitions'
import { OneWaitlistDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CrossellingSelector from 'modules/upsell/components/SelfUpsell/CheckoutModal/CrossellingSelector'
import LeftSection from 'modules/upsell/components/SelfUpsell/CheckoutModal/LeftSection'
import VariantSelector from 'modules/upsell/components/SelfUpsell/CheckoutModal/VariantSelector'
import type Plan from 'modules/upsell/lib/selfUpsell/plans/Plan'
import { MONTH_PERIOD, SIX_MONTH_PERIOD } from 'modules/upsell/lib/selfUpsell/plans/utils'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type FirstStepProps = {
  addon: Plan
  crossSellingAddon?: Plan | undefined
  step: string
  setStep: (step: string) => void
  setSelectedAddonId: (id: string) => void
  setSelectedCrossSellingAddonId: (id: string) => void
  handleAssistance: () => void
  moduleName: AvailableModules
}

const FirstStep = ({
  addon,
  crossSellingAddon,
  step,
  setStep,
  setSelectedAddonId,
  handleAssistance,
  moduleName,
}: FirstStepProps) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [open, toggle] = useToggle(false)
  const { period, configPlan, seats, currentSeats, shouldBypassSelfUpsell } = useSelfUpsellContext()
  const isValidSubscription = shouldBypassSelfUpsell || period !== SIX_MONTH_PERIOD
  const { isMobile } = useBreakpoint()

  const { data: oneWaitlistData } = useQuery(OneWaitlistDocument, {
    variables: {
      companyId: companyId,
    },
  })

  const handleSubmit = () => {
    track(trackingEvents.upsell.clickReviewOrderButton, {
      module: moduleName,
      selectedPlan: configPlan.variant,
      seats: seats,
      billingPeriod: period,
    })
    setStep('second')
  }

  const crossSellingAlreadyInstalled =
    oneWaitlistData?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id === 'one_core'
    )?.available ?? false

  return (
    <Box flexDirection="row" height="full">
      {!isMobile && (
        <LeftSection addon={addon} variant={configPlan.variant} step={step} iconName={moduleName} />
      )}
      <Box
        padding="s24"
        justifyContent="spaceBetween"
        style={{
          width: isMobile ? '100%' : '60%',
        }}
      >
        <Box gap="s24">
          <Box height="s24" />
          <Box gap="s32">
            {isMobile && (
              <Text size="400" color="grey1000" weight="semibold">
                {addon.title}
              </Text>
            )}
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('self_upsell.first_step.choose_plan')}
            </Text.Normal>
            <VariantSelector
              addon={addon}
              crossSellingAddon={!crossSellingAlreadyInstalled ? crossSellingAddon : undefined}
              showPrice={isValidSubscription}
              setSelectedAddonId={setSelectedAddonId}
            />

            {crossSellingAddon && !crossSellingAlreadyInstalled && (
              <Box gap="s8">
                <Text.Normal weight="semibold" color="grey1000">
                  {i18n.t('self_upsell.first_step.special_offer')}
                </Text.Normal>
                <Text.Normal size="100" color="grey700">
                  {i18n.t('self_upsell.first_step.one_special_offer')}
                </Text.Normal>
                <CrossellingSelector
                  crossSellingAddon={crossSellingAddon}
                  showPrice={isValidSubscription}
                />
              </Box>
            )}
          </Box>
        </Box>

        {isValidSubscription ? (
          <Box gap="s16" flexDirection="row" justifyContent="spaceBetween" alignItems="center">
            <Box gap="s8">
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Text.Normal size="200" color="grey700" weight="medium">
                  {i18n.t('self_upsell.first_step.your_subscription')}
                </Text.Normal>
                <Box alignSelf="center">
                  <Tooltip.Alt
                    size="small"
                    attachment="bottom center"
                    open={open}
                    toggle={toggle}
                    label={i18n.t('self_upsell.first_step.tooltip')}
                  >
                    <Icon.Medium icon="info-circle-line" />
                  </Tooltip.Alt>
                </Box>
              </Box>
              <Box flexDirection="row" alignItems="center" gap="s8">
                <Box flexDirection="row" alignItems="center" gap="s4">
                  <Icon.Small icon="people" />
                  <Text.Normal size="200">
                    {i18n.t('self_upsell.common.seats', {
                      seats: currentSeats,
                    })}
                  </Text.Normal>
                </Box>
                <Box flexDirection="row" alignItems="center" gap="s4">
                  <Icon.Small icon="calendar" />
                  <Text.Normal size="200">
                    {period === MONTH_PERIOD
                      ? i18n.t('self_upsell.first_step.monthly')
                      : i18n.t('self_upsell.first_step.yearly')}
                  </Text.Normal>
                </Box>
              </Box>
            </Box>
            <Box alignSelf="flexEnd">
              <F0Button
                size="lg"
                label={i18n.t('self_upsell.first_step.button')}
                onClick={handleSubmit}
                disabled={!configPlan.variant}
              />
            </Box>
          </Box>
        ) : (
          <Box flexDirection="row" gap="s8" justifyContent="spaceBetween">
            <Text.Normal size="100" weight="medium">
              {i18n.t('self_upsell.first_step.assistance_description')}
            </Text.Normal>
            <Box alignSelf="flexEnd">
              <F0Button
                size="md"
                label={i18n.t('self_upsell.common.request_assistance')}
                onClick={handleAssistance}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FirstStep
